/**
 * env.ts — the environment metal reflects.
 *
 * A `MeshStandardMaterial` with high metalness and nothing to reflect is not
 * shiny steel, it is black plastic with a specular dot on it. This builds an
 * equirectangular image of a torchlit stone hall and runs it through
 * `PMREMGenerator`.
 *
 * It is GENERATED, not loaded. Same rule as the audio and the fonts: nothing
 * here can 404 on a fresh deploy.
 *
 * Surface bump maps live in `surfaces.ts` and are re-exported here so older
 * imports keep working.
 */

import * as THREE from 'three';

export { leatherSurface, steelSurface } from './surfaces.js';

/**
 * Equirectangular map of a torchlit stone hall.
 *
 * Resolution is higher than the old 64×2 map so mid-roughness steel picks up
 * readable structure; PMREM still blurs the mips for rough surfaces.
 */
function hallEquirect(): THREE.DataTexture {
  const width = 256;
  const height = 128;
  const data = new Uint8Array(width * height * 4);

  for (let y = 0; y < height; y += 1) {
    const v = y / (height - 1);
    for (let x = 0; x < width; x += 1) {
      const u = x / width;

      // Ceiling: nearly black. Floor: warm stone catching the torches.
      let r = 0.04;
      let g = 0.036;
      let b = 0.032;
      if (v > 0.58) {
        const t = (v - 0.58) / 0.42;
        r = 0.04 + t * 0.28;
        g = 0.036 + t * 0.2;
        b = 0.032 + t * 0.12;
        // Flagstone mottling on the floor band.
        const mott = Math.abs(Math.sin(u * Math.PI * 18) * Math.sin(v * Math.PI * 10));
        r += mott * 0.04;
        g += mott * 0.03;
        b += mott * 0.02;
      }

      // Eight torch lobes around the walls — warmer, structured highlights.
      const band = Math.exp(-((v - 0.5) ** 2) / 0.007);
      const torch = Math.pow(Math.abs(Math.cos(u * Math.PI * 8)), 28);
      r += band * (0.1 + torch * 1.65);
      g += band * (0.065 + torch * 0.95);
      b += band * (0.04 + torch * 0.38);

      // Cool grate spill from above.
      const grate = Math.exp(-(v ** 2) / 0.04) * 0.42;
      const grateBars = 0.55 + 0.45 * Math.pow(Math.abs(Math.cos(u * Math.PI * 12)), 4);
      r += grate * 0.22 * grateBars;
      g += grate * 0.3 * grateBars;
      b += grate * 0.48 * grateBars;

      // Soft wall midtone so metal does not go dead between torches.
      const wall = Math.exp(-((v - 0.42) ** 2) / 0.04) * 0.12;
      r += wall * 0.35;
      g += wall * 0.28;
      b += wall * 0.2;

      const i = (y * width + x) * 4;
      data[i] = Math.min(255, r * 255);
      data[i + 1] = Math.min(255, g * 255);
      data[i + 2] = Math.min(255, b * 255);
      data[i + 3] = 255;
    }
  }

  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Builds the scene environment for a renderer.
 *
 * PMREM output is tied to the renderer that made it, so this takes one rather
 * than caching a single texture module-wide.
 */
export function createEnvironment(renderer: THREE.WebGLRenderer): THREE.Texture | undefined {
  try {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const source = hallEquirect();
    const target = pmrem.fromEquirectangular(source);
    source.dispose();
    pmrem.dispose();
    return target.texture;
  } catch {
    return undefined;
  }
}
